import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

import { GetCollectionReport } from "../../api/HostelService";
import { getBatchList } from "../../api/BatchService";
import { getSessions } from "../../api/SessionsService";
import { BankList } from "../../api/BankService";

import HostelHeader from "./HostelHeader";
import Nav from "../master/NavBar";
import Footer from "../main/footer";

import { formatDate, SweetAlertShow, LoadingDiv } from "../master/Master";

import WelcomeBar from "./WelcomeBar";
import usePrivateLink from "../config/usePrivateLink";

import { studentFeeReceipt } from "../../api/Student";
import FeeReceipt1 from "../components/FeeReceipt1";
import { printElement } from "../master/Print";
import HostelFeeReceipt from "../components/HostelFeeReceipt";
import HostelReportTabs from "./HostelReportTabs";

export default function HostelFeeReport() {
  const [page, setPage] = useState(1);
  const [Data, setData] = useState(null);
  const [total, setTotal] = useState(0);

  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);

  const [name, setName] = useState("");
  const [batchId, setBatchID] = useState("");
  const [staffId, setStaffId] = useState("");

  const [loading, setLoading] = useState(false);

  const [selectedSession, setSelectedSessions] = useState("");
  const [sessions, setSessions] = useState([]);

  const [error, setError] = useState("");
  const [transition, setTransition] = useState([]);

  const [type, setType] = useState("");

  const [banks, setBanks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");

  const [status, setStatus] = useState(1);

  const [hostel_id, setHostelId] = useState(localStorage.getItem("hostel_id"));

  const loadOnce = useRef(false);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState({});
  const [receiptLoading, setReceiptLoading] = useState(false);

  const hasAccess = usePrivateLink();

  /* =========================================================
     HOSTEL
  ========================================================= */

  useEffect(() => {
    setHostelId(localStorage.getItem("hostel_id"));
  }, []);

  /* =========================================================
     BANK LIST
  ========================================================= */

  const getBusinessBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Something went wrong",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessBanks();
  }, []);

  /* =========================================================
     DEFAULT DATES
  ========================================================= */

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDefaultDate = (date) => {
      if (!(date instanceof Date)) {
        date = new Date(date);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDefaultDate(firstDayOfMonth),
      to: formatDefaultDate(today),
    };
  };

  const { from: defaultFrom, to: defaultTo } = getDefaultDates();

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  /* =========================================================
     SESSION
  ========================================================= */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionList = response?.data?.data || [];

        const activeSession = sessionList.find(
          (session) => session.status === "1",
        );

        if (activeSession) {
          setSelectedSessions(activeSession.id);
        } else if (sessionList.length > 0) {
          setSelectedSessions(sessionList[0].id);
        }

        setSessions(sessionList);
      } else {
        setError("Failed to fetch session.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  /* =========================================================
     EXPORT
     EXISTING API PARAMETERS PRESERVED
  ========================================================= */

  const handleExportAll = async () => {
    try {
      setLoading(true);

      let addUrl = `&page=${page}&session_id=${selectedSession}`;

      if (from) addUrl += `&start_date=${from}`;
      if (to) addUrl += `&end_date=${to}`;
      if (batchId) addUrl += `&batch_id=${batchId}`;
      if (staffId) addUrl += `&staff_id=${staffId}`;
      if (name) addUrl += `&search=${name}`;
      if (type) addUrl += `&type=${type}`;
      if (selectedAccount) {
        addUrl += `&bank_id=${selectedAccount}`;
      }

      let allData = [];

      const firstResponse = await GetCollectionReport(`${addUrl}&page=1`);

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      for (let currentPage = 1; currentPage <= lastPage; currentPage++) {
        const response = await GetCollectionReport(
          `${addUrl}&page=${currentPage}`,
        );

        const records = response?.data?.data?.data || [];

        allData.push(...records);
      }

      if (allData.length === 0) {
        alert("No data to export!");
        return;
      }

      const formattedData = allData.map((item) => ({
        "Hostel Name": item?.hostel?.name || "",

        "Hostel Address": item?.hostel?.address || "",

        "Student Name":
          item?.student?.name || item?.hostel_student?.student_name || "",

        "Father Name":
          item?.student?.father_name || item?.hostel_student?.father_name || "",

        "Mobile Number":
          item?.student?.phone || item?.hostel_student?.mobile || "",

        "Room ID": item?.hostel_student?.room_id || "",

        "Bed ID": item?.hostel_student?.bed_id || "",

        "Total Fee": item?.hostel_student?.total_fee || 0,

        "Final Fee": item?.hostel_student?.final_fee || 0,

        "Paid Fee": item?.hostel_student?.paid_fee || 0,

        "Amount Paid": item?.amount || 0,

        "Transaction ID": item?.transaction_no || "",

        "Payment Date": item?.payment_date || "",

        "Next Payment Date": item?.next_payment_date || "",

        Remark: item?.remark || "",

        "Bank Name": item?.bank?.owner_name || "",

        "Bank Branch": item?.bank?.branch || "",

        "Bank Account No": item?.bank?.account_no || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Hostel Collection");

      XLSX.writeFile(workbook, "HostelCollectionReport.xlsx");

      alert(`Exported ${allData.length} records successfully!`);
    } catch (error) {
      console.error(error);

      alert(`Error in exporting: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FETCH DATA
     EXISTING API PARAMETERS PRESERVED
  ========================================================= */

  const fetchData = async () => {
    setLoading(true);

    let addUrl = `&page=${page}&session_id=${selectedSession}`;

    if (from) addUrl += `&start_date=${from}`;
    if (to) addUrl += `&end_date=${to}`;
    // if (batchId) addUrl += `&batch_id=${batchId}`;
    if (staffId) addUrl += `&staff_id=${staffId}`;
    if (name) addUrl += `&search=${name}`;
    if (status) addUrl += `&status=${status}`;
    if (selectedAccount) {
      addUrl += `&bank_id=${selectedAccount}`;
    }

    try {
      const response = await GetCollectionReport(addUrl);

      setData(response?.data?.data);

      setTransition(response?.data?.data || []);

      setTotal(response?.data?.total_sum || 0);
    } catch (error) {
      console.error("Failed to fetch collection data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     BATCHES
  ========================================================= */

  const getBatches = async () => {
    const addUrl = `&session_id=${selectedSession}`;

    setLoading(true);

    try {
      const response = await getBatchList(addUrl);

      setBatches(response?.data?.batches || []);
    } catch (error) {
      console.error("Failed to fetch batches:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DATA EFFECTS
  ========================================================= */

  // useEffect(() => {
  //   getBatches();
  // }, [selectedSession]);

  useEffect(() => {
    if (!selectedSession) return;

    fetchData();
  }, [
    page,
    from,
    to,
    batchId,
    staffId,
    name,
    selectedSession,
    status,
    selectedAccount,
  ]);

  /* =========================================================
     HELPERS
  ========================================================= */

  const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getCurrentSessionName = () => {
    const session = sessions?.find(
      (item) => String(item?.id) === String(selectedSession),
    );

    return session?.session || "-";
  };

  const getSelectedBatchName = () => {
    if (!batchId) {
      return "All Batches";
    }

    const batch = batches?.find((item) => String(item?.id) === String(batchId));

    return batch?.title || "Selected Batch";
  };

  const getSelectedBankName = () => {
    if (!selectedAccount) {
      return "All Accounts";
    }

    const bank = banks?.find(
      (item) => String(item?.id) === String(selectedAccount),
    );

    if (!bank) {
      return "Selected Account";
    }

    return `${bank?.account_no || ""} (${bank?.owner_name || ""})`;
  };

  const resetFilters = () => {
    setFrom(defaultFrom);
    setTo(defaultTo);
    setBatchID("");
    setStaffId("");
    setName("");
    setSelectedAccount("");
    setPage(1);
  };

  const hasActiveFilters =
    name ||
    batchId ||
    staffId ||
    selectedAccount ||
    from !== defaultFrom ||
    to !== defaultTo;

  const handlePrintReceipt = async (id) => {
    try {
      setReceiptLoading(true);
      setShowReceiptModal(true);

      const response = await studentFeeReceipt(id);

      if (response?.data?.status) {
        setReceiptData(response.data.data);
      } else {
        setShowReceiptModal(false);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Receipt data not found",
        );
      }
    } catch (error) {
      setShowReceiptModal(false);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load receipt",
      );
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <>
      {/* =========================================================
          LOADING
      ========================================================= */}

      {loading && <LoadingDiv />}

      {/* =========================================================
          LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <HostelHeader />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* <WelcomeBar /> */}

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 bg-label-success d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                      }}
                    >
                      <i className="ri-money-rupee-circle-line fs-4 text-success" />
                    </div>

                    <div>
                      <h4 className="mb-1 fw-bold">Fee Collection Report</h4>

                      <p className="mb-0 text-muted">
                        View and manage hostel fee collection transactions
                      </p>
                    </div>
                  </div>

                  {/* <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-label-primary px-3 py-2">
                      <i className="ri-calendar-check-line me-1" />

                      {getCurrentSessionName()}
                    </span>
                  </div> */}
                </div>

                {/* =================================================
                    REPORT NAVIGATION
                ================================================= */}

                {/* <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {hasAccess("/hostel/reports") && (
                        <Link
                          to="/hostel/reports"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-dashboard-line me-1" />
                          Overview
                        </Link>
                      )}

                      {hasAccess("/hostel/credit/debit/report") && (
                        <Link
                          to="/hostel/credit/debit/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-exchange-dollar-line me-1" />
                          Credit Debit Report
                        </Link>
                      )}

                      {hasAccess("/hostel/fee/report") && (
                        <Link
                          to="/hostel/fee/report"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="ri-money-rupee-circle-line me-1" />
                          Fee Report
                        </Link>
                      )}

                      {hasAccess("/hostel/expense/report") && (
                        <Link
                          to="/hostel/expense/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-wallet-3-line me-1" />
                          Expense Report
                        </Link>
                      )}

                      {hasAccess("/hostel/fee/due/report") && (
                        <Link
                          to="/hostel/fee/due/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-error-warning-line me-1" />
                          Due Fee Report
                        </Link>
                      )}

                      {hasAccess("/hostel/student/month/report") && (
                        <Link
                          to="/hostel/student/month/report"
                          className="btn btn-sm btn-label-primary"
                        >
                          <i className="ri-calendar-line me-1" />
                          Hostel Student Report
                        </Link>
                      )}


                      <div className="ms-lg-auto">
                        <div
                          className="input-group input-group-sm"
                          style={{
                            minWidth: 220,
                          }}
                        >
                          <span className="input-group-text">
                            <i className="ri-calendar-check-line" />
                          </span>

                          <select
                            className="form-select"
                            value={selectedSession}
                            onChange={(e) => {
                              setSelectedSessions(e.target.value);
                              setPage(1);
                            }}
                          >
                            {sessions?.map((session) => (
                              <option key={session?.id} value={session?.id}>
                                {session?.session}
                                {" - "}
                                {session?.status == "1" ? "Active" : "Inactive"}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                <HostelReportTabs />

                {/* =================================================
                    FILTER CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header border-bottom py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-2 bg-label-primary d-flex align-items-center justify-content-center"
                          style={{
                            width: 38,
                            height: 38,
                          }}
                        >
                          <i className="ri-filter-3-line text-primary fs-5" />
                        </div>

                        <div>
                          <h5 className="mb-0">Filter Collection</h5>

                          <small className="text-muted">
                            Refine collection records using the available
                            filters
                          </small>
                        </div>
                      </div>

                      {hasActiveFilters && (
                        <button
                          type="button"
                          className="btn btn-sm btn-text-secondary"
                          onClick={resetFilters}
                        >
                          <i className="ri-refresh-line me-1" />
                          Reset
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {/* FROM DATE */}

                      <div className="col-md-6 col-xl-3">
                        <label className="form-label fw-semibold">
                          From Date
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-calendar-line" />
                          </span>

                          <input
                            type="date"
                            className="form-control"
                            value={from}
                            onChange={(e) => {
                              setFrom(e.target.value);
                              setPage(1);
                            }}
                          />
                        </div>
                      </div>

                      {/* TO DATE */}

                      <div className="col-md-6 col-xl-3">
                        <label className="form-label fw-semibold">
                          To Date
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-calendar-check-line" />
                          </span>

                          <input
                            type="date"
                            className="form-control"
                            value={to}
                            min={from || undefined}
                            onChange={(e) => {
                              setTo(e.target.value);
                              setPage(1);
                            }}
                          />
                        </div>
                      </div>

                      {/* BATCH */}

                      {/* <div className="col-md-6 col-xl-3">
                        <label className="form-label fw-semibold">Batch</label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-group-line" />
                          </span>

                          <select
                            className="form-select"
                            value={batchId}
                            onChange={(e) => {
                              setBatchID(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Batches</option>

                            {batches?.map((batch, i) => {
                              const courseTitle = batch?.course?.title || "";

                              const shortCourseTitle =
                                courseTitle.length > 20
                                  ? courseTitle.substring(0, 20) + "..."
                                  : courseTitle;

                              return (
                                <option key={i} value={batch?.id}>
                                  {batch?.title}
                                  {shortCourseTitle
                                    ? ` (${shortCourseTitle})`
                                    : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div> */}

                      {/* ACCOUNT */}

                      <div className="col-md-6 col-xl-3">
                        <label className="form-label fw-semibold">
                          Account
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-bank-line" />
                          </span>

                          <select
                            name="account_no"
                            className="form-select"
                            value={selectedAccount}
                            onChange={(e) => {
                              setSelectedAccount(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Accounts</option>

                            {banks?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.account_no}
                                {" - "}
                                {item?.owner_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* SEARCH */}

                      <div className="col-md-8 col-xl-6">
                        <label className="form-label fw-semibold">
                          Student Search
                        </label>

                        <div className="input-group input-group-merge">
                          <span className="input-group-text">
                            <i className="ri-search-line" />
                          </span>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Student name, mobile or registration no."
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              setPage(1);
                            }}
                          />

                          {name && (
                            <button
                              type="button"
                              className="btn btn-text-secondary"
                              onClick={() => {
                                setName("");
                                setPage(1);
                              }}
                            >
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* FILTER SUMMARY */}

                      <div className="col-md-4 col-xl-6">
                        <label className="form-label fw-semibold">
                          Current Filter
                        </label>

                        <div
                          className="rounded-3 bg-label-primary d-flex align-items-center px-3"
                          style={{
                            minHeight: 44,
                          }}
                        >
                          <i className="ri-filter-line text-primary me-2" />

                          <div className="d-flex flex-wrap align-items-center gap-2">
                            {from && (
                              <span className="badge bg-label-primary">
                                {from}
                              </span>
                            )}

                            {to && (
                              <>
                                <span className="text-muted">→</span>

                                <span className="badge bg-label-primary">
                                  {to}
                                </span>
                              </>
                            )}

                            {batchId && (
                              <span className="badge bg-label-info">
                                {getSelectedBatchName()}
                              </span>
                            )}

                            {selectedAccount && (
                              <span className="badge bg-label-success">
                                {getSelectedBankName()}
                              </span>
                            )}

                            {!hasActiveFilters && (
                              <span className="text-primary fw-semibold">
                                All Collections
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    SUMMARY
                ================================================= */}

                <div className="row g-3 mb-4">
                  {/* TOTAL COLLECTION */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              Total Collection
                            </small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              ₹{formatAmount(total)}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-success p-3">
                            <i className="ri-money-rupee-circle-line fs-4 text-success" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Total amount collected for the selected filters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RECORDS */}

                  <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">
                              Collection Records
                            </small>

                            <h4 className="mb-0 mt-1 fw-bold">
                              {Data?.total || transition?.length || 0}
                            </h4>
                          </div>

                          <div className="rounded-3 bg-label-primary p-3">
                            <i className="ri-file-list-3-line fs-4 text-primary" />
                          </div>
                        </div>

                        <div className="mt-3">
                          <small className="text-muted">
                            Transactions matching current filters
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* EXPORT */}

                  <div className="col-md-12 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <small className="text-muted">Report Export</small>

                            <h6 className="mb-1 mt-1 fw-bold">
                              Download Collection Report
                            </h6>

                            <small className="text-muted">
                              Export all matching collection records
                            </small>
                          </div>

                          {hasAccess("/hostel/credit/debit/report/export") && (
                            <button
                              type="button"
                              className="btn btn-success d-flex align-items-center gap-2"
                              onClick={handleExportAll}
                              title="Export Excel"
                            >
                              <i className="ri-file-excel-2-line fs-5" />

                              <span>Export</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    COLLECTION TABLE
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  {/* TABLE HEADER */}

                  <div className="card-header border-bottom py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0 fw-bold">
                            Collection Transactions
                          </h5>

                          {/* <span
                            className={`badge ${
                              status == "3"
                                ? "bg-label-danger"
                                : "bg-label-success"
                            }`}
                          >
                            {status == "3" ? "Deleted" : "Collection"}
                          </span> */}
                        </div>

                        <small className="text-muted">
                          Student-wise fee collection transaction history
                        </small>
                      </div>

                      {/* STATUS TOGGLE */}

                      {/* <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            status == "1" ? "btn-success" : "btn-label-success"
                          }`}
                          onClick={() => {
                            setStatus(1);
                            setPage(1);
                          }}
                        >
                          <i className="ri-checkbox-circle-line me-1" />
                          Collection
                        </button>

                        {hasAccess("/deleted-collection") && (
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              status == "3" ? "btn-danger" : "btn-label-danger"
                            }`}
                            onClick={() => {
                              setStatus(3);
                              setPage(1);
                            }}
                          >
                            <i className="ri-delete-bin-line me-1" />
                            Deleted
                          </button>
                        )}
                      </div> */}
                    </div>
                  </div>

                  {/* TABLE */}

                  <div className="card-body p-0">
                    {Data?.data?.length > 0 ? (
                      <div
                        className="table-responsive"
                        style={{
                          maxHeight: "65vh",
                          overflow: "auto",
                        }}
                      >
                        <table className="table table-hover align-middle mb-0">
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                            }}
                          >
                            <tr className="text-center">
                              <th
                                style={{
                                  minWidth: 60,
                                }}
                              >
                                #
                              </th>

                              <th
                                className="text-start"
                                style={{
                                  minWidth: 220,
                                }}
                              >
                                Student
                              </th>

                              <th
                                style={{
                                  minWidth: 170,
                                }}
                              >
                                Mobile
                              </th>

                              <th
                                style={{
                                  minWidth: 160,
                                }}
                              >
                                Hostel
                              </th>

                              <th
                                style={{
                                  minWidth: 200,
                                }}
                              >
                                Payment
                              </th>

                              <th
                                style={{
                                  minWidth: 220,
                                }}
                              >
                                Remark
                              </th>

                              <th
                                style={{
                                  minWidth: 130,
                                }}
                              >
                                Date
                              </th>

                              <th
                                style={{
                                  minWidth: 230,
                                }}
                              >
                                Bank
                              </th>

                              <th
                                style={{
                                  minWidth: 150,
                                }}
                              >
                                Amount
                              </th>

                              <th
                                style={{
                                  minWidth: 100,
                                }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {Data?.data?.map((data, i) => {
                              const student = data?.hostel_student || {};

                              const bank = data?.bank || {};

                              return (
                                <tr key={data?.id || i}>
                                  {/* INDEX */}

                                  <td className="text-center">
                                    <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-secondary fw-semibold"
                                      style={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                  </td>

                                  {/* STUDENT */}

                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                                          status == "3"
                                            ? "bg-label-danger"
                                            : "bg-label-primary"
                                        }`}
                                        style={{
                                          width: 42,
                                          height: 42,
                                        }}
                                      >
                                        <i
                                          className={`ri-user-line fs-5 ${
                                            status == "3"
                                              ? "text-danger"
                                              : "text-primary"
                                          }`}
                                        />
                                      </div>

                                      <div>
                                        <div className="fw-semibold">
                                          {student?.student_name ||
                                            data?.student?.name ||
                                            "-"}
                                        </div>

                                        {student?.registration_no && (
                                          <small className="text-muted">
                                            Reg. No: {student.registration_no}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* MOBILE */}

                                  <td className="text-center">
                                    <span className="fw-medium">
                                      {student?.mobile ||
                                        data?.student?.phone ||
                                        "-"}
                                    </span>
                                  </td>

                                  {/* HOSTEL */}
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <i className="ri-building-line text-muted" />

                                      <span>
                                        {data?.hostel_student?.hostel?.name ||
                                          "-"}
                                      </span>
                                    </div>
                                  </td>

                                  {/* PAYMENT */}

                                  <td className="text-center">
                                    <div className="d-flex flex-column align-items-center gap-1">
                                      <span className="badge bg-label-primary px-3 py-2">
                                        <i className="ri-bank-card-line me-1" />

                                        {data?.payment_method || "N/A"}
                                      </span>

                                      {data?.transaction_no && (
                                        <small
                                          className="text-muted text-truncate"
                                          style={{
                                            maxWidth: 180,
                                          }}
                                          title={data.transaction_no}
                                        >
                                          TXN: {data.transaction_no}
                                        </small>
                                      )}
                                    </div>
                                  </td>

                                  {/* REMARK */}

                                  <td>
                                    <div
                                      className="text-truncate"
                                      style={{
                                        maxWidth: 210,
                                      }}
                                      title={data?.remark || ""}
                                    >
                                      {data?.remark || "No remark"}
                                    </div>
                                  </td>

                                  {/* DATE */}

                                  <td className="text-center">
                                    <span className="fw-medium">
                                      {formatDate(data?.payment_date)}
                                    </span>
                                  </td>

                                  {/* BANK */}

                                  <td className="text-center">
                                    {bank?.account_no ? (
                                      <div>
                                        <div className="d-flex align-items-center justify-content-center gap-1 fw-semibold">
                                          <i className="ri-bank-line text-primary" />

                                          <span>{bank.account_no}</span>
                                        </div>

                                        <small className="text-muted">
                                          {bank?.owner_name || "Account"}
                                        </small>
                                      </div>
                                    ) : (
                                      <span className="badge bg-label-secondary px-3 py-2">
                                        <i className="ri-money-cash-line me-1" />
                                        Cash / N/A
                                      </span>
                                    )}
                                  </td>

                                  {/* AMOUNT */}

                                  <td className="text-end">
                                    <span
                                      className={`badge px-3 py-2 fw-bold ${
                                        status == "3"
                                          ? "bg-label-danger"
                                          : "bg-label-success"
                                      }`}
                                    >
                                      ₹{formatAmount(data?.amount)}
                                    </span>
                                  </td>

                                  {/* ACTION */}

                                  <td className="text-center">
                                    {status == "1" && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-label-primary"
                                        title="Print Receipt"
                                        onClick={() =>
                                          handlePrintReceipt(data?.id)
                                        }
                                      >
                                        <i className="ri-printer-line me-1" />
                                        Print
                                      </button>
                                    )}

                                    {status == "3" && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-label-danger"
                                        disabled
                                        title="Deleted collection"
                                      >
                                        <i className="ri-delete-bin-line me-1" />
                                        Deleted
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* =================================================
                         EMPTY STATE
                      ================================================= */

                      <div className="text-center py-5 px-3">
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 ${
                            status == "3"
                              ? "bg-label-danger"
                              : "bg-label-secondary"
                          }`}
                          style={{
                            width: 72,
                            height: 72,
                          }}
                        >
                          <i
                            className={`fs-2 ${
                              status == "3"
                                ? "ri-delete-bin-line text-danger"
                                : "ri-receipt-line text-muted"
                            }`}
                          />
                        </div>

                        <h5 className="fw-bold mb-2">
                          {status == "3"
                            ? "No Deleted Collections"
                            : "No Collection Records"}
                        </h5>

                        <p className="text-muted mb-3">
                          {status == "3"
                            ? "No deleted collection records were found for the selected filters."
                            : "No fee collection transactions were found for the selected filters."}
                        </p>

                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="btn btn-sm btn-label-primary"
                            onClick={resetFilters}
                          >
                            <i className="ri-refresh-line me-1" />
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      PAGINATION
                  ================================================= */}

                  {Data?.links?.length > 0 && (
                    <div className="card-footer border-top">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                          <small className="text-muted">
                            Showing <strong>{Data?.from || 0}</strong> to{" "}
                            <strong>{Data?.to || 0}</strong> of{" "}
                            <strong>{Data?.total || 0}</strong> entries
                          </small>
                        </div>

                        <nav aria-label="Page navigation">
                          <ul className="pagination pagination-sm mb-0">
                            {Data?.links?.map((btn, i) => (
                              <li
                                key={`${btn?.label}-${i}`}
                                className={`page-item ${
                                  btn?.active ? "active" : ""
                                } ${btn?.url ? "" : "disabled"}`}
                              >
                                <button
                                  type="button"
                                  className="page-link waves-effect"
                                  onClick={() => {
                                    if (Data?.links?.length === i + 1) {
                                      btn?.url &&
                                        setPage(Data?.current_page + 1);
                                    } else if (i === 0) {
                                      btn?.url &&
                                        setPage(Data?.current_page - 1);
                                    } else {
                                      setPage(Number(btn?.label));
                                    }
                                  }}
                                >
                                  {Data?.links?.length === i + 1 ? (
                                    <i className="ri-arrow-right-line" />
                                  ) : i === 0 ? (
                                    <i className="ri-arrow-left-line" />
                                  ) : (
                                    btn?.label
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Footer />

              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
      {/* =========================================================
    FEE RECEIPT MODAL
========================================================= */}

      {showReceiptModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content border-0 shadow-lg">
              {/* MODAL HEADER */}
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="ri-receipt-line me-2 text-primary" />
                    Fee Receipt
                  </h5>

                  <small className="text-muted">
                    Preview and print fee receipt
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowReceiptModal(false);
                    setReceiptData({});
                  }}
                />
              </div>

              {/* MODAL BODY */}
              <div className="modal-body bg-light">
                {receiptLoading ? (
                  <div
                    className="d-flex flex-column justify-content-center align-items-center"
                    style={{ minHeight: 400 }}
                  >
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>

                    <div className="text-muted">Loading receipt...</div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center">
                    <div
                      id="FeeReceiptPrintArea"
                      className="bg-white shadow-sm"
                      style={{
                        width: "100%",
                        maxWidth: "800px",
                        padding: "20px",
                      }}
                    >
                      <HostelFeeReceipt data={receiptData} />
                    </div>
                  </div>
                )}
              </div>

              {/* MODAL FOOTER */}
              {!receiptLoading && (
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-label-secondary"
                    onClick={() => {
                      setShowReceiptModal(false);
                      setReceiptData({});
                    }}
                  >
                    <i className="ri-close-line me-1" />
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => printElement("FeeReceiptPrintArea")}
                  >
                    <i className="ri-printer-line me-1" />
                    Print Receipt
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL BACKDROP */}
      {showReceiptModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            setShowReceiptModal(false);
            setReceiptData({});
          }}
        />
      )}
    </>
  );
}
